/**
 * Tiny WebAudio beep generator for the kiosk overlay.
 *
 * iOS quirk per CLAUDE.md §22: AudioContext can ONLY be created/resumed
 * inside a user-gesture handler. We lazy-init on first call and silently
 * skip playback if the page hasn't seen a tap yet — the visual overlay still
 * works, only the sound is delayed until interaction.
 */

export type SoundKey =
  | 'check-in-anwesend'
  | 'check-in-verspaetet'
  | 'check-in-troedel-orange'
  | 'check-in-troedel-rot'
  | 'check-out'
  | 'error';

interface Beep {
  /** Frequency in Hz */
  freq: number;
  /** Duration in seconds */
  duration: number;
  /** Pause after the beep, in seconds */
  pause?: number;
  /** Volume 0..1 */
  gain?: number;
  /** Waveform; defaults to "sine" */
  type?: OscillatorType;
}

const SOUND_PATTERNS: Record<SoundKey, Beep[]> = {
  'check-in-anwesend': [{ freq: 1320, duration: 0.12 }],
  'check-in-verspaetet': [
    { freq: 1100, duration: 0.1 },
    { freq: 1100, duration: 0.1, pause: 0.08 },
  ],
  'check-in-troedel-orange': [
    { freq: 1320, duration: 0.1 },
    { freq: 880, duration: 0.18, pause: 0.08 },
  ],
  'check-in-troedel-rot': [
    { freq: 1320, duration: 0.1 },
    { freq: 740, duration: 0.18, pause: 0.06 },
    { freq: 740, duration: 0.18, pause: 0.06 },
  ],
  'check-out': [{ freq: 440, duration: 0.5 }],
  error: [
    { freq: 220, duration: 0.18, type: 'sawtooth', gain: 0.18 },
    { freq: 180, duration: 0.18, type: 'sawtooth', gain: 0.18, pause: 0.05 },
  ],
};

let ctx: AudioContext | null = null;
let userInteracted = false;

/** Marks that a user gesture occurred, so subsequent `playSound` calls work on iOS. */
export function markUserInteracted(): void {
  userInteracted = true;
}

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  if (!userInteracted) return null;
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

function playBeep(audio: AudioContext, beep: Beep, startAt: number): number {
  const osc = audio.createOscillator();
  const gainNode = audio.createGain();
  osc.frequency.value = beep.freq;
  osc.type = beep.type ?? 'sine';
  const peak = beep.gain ?? 0.22;
  // Tiny attack/release ramp so beeps don't click.
  gainNode.gain.setValueAtTime(0, startAt);
  gainNode.gain.linearRampToValueAtTime(peak, startAt + 0.01);
  gainNode.gain.linearRampToValueAtTime(0, startAt + beep.duration);
  osc.connect(gainNode).connect(audio.destination);
  osc.start(startAt);
  osc.stop(startAt + beep.duration + 0.02);
  return startAt + beep.duration + (beep.pause ?? 0.04);
}

/**
 * Play one of the kiosk sounds. Best-effort: returns silently if the
 * AudioContext isn't usable yet (e.g. before first tap on iOS).
 */
export function playSound(key: SoundKey): void {
  const audio = getCtx();
  if (!audio) return;
  // Some browsers leave the context "suspended" after creation; nudge it.
  if (audio.state === 'suspended') {
    void audio.resume().catch(() => {
      /* ignore */
    });
  }
  const pattern = SOUND_PATTERNS[key];
  let cursor = audio.currentTime + 0.01;
  for (const beep of pattern) {
    cursor = playBeep(audio, beep, cursor);
  }
}

/** Test-only reset. */
export function _resetKioskAudio(): void {
  ctx = null;
  userInteracted = false;
}
